/**
 * Bearer-token links from marketing email (the no-login preference page).
 *
 * The token in `/email-preferences?token=…` is a credential. Before anything
 * else runs (analytics boot in src/index.js), the token is moved into memory and
 * removed from the address bar with history.replaceState, so it never reaches
 * GA/Clarity page views, browser history sync, or a Referer header. Analytics
 * also never loads on these paths at all.
 */

const TOKEN_PAGE_PATHS = ['/email-preferences'];
// Backend-rendered unsubscribe pages; listed so a client-side check can never
// enable analytics there either.
const SERVER_TOKEN_PATH_PREFIXES = ['/api/v1/marketing/', '/api/v1/campaigns/'];

let tokenInMemory = null;

const matchesPage = (pathname) =>
  TOKEN_PAGE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export function isAnalyticsExcludedPath(pathname) {
  const path = String(pathname || '');
  return matchesPage(path) || SERVER_TOKEN_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Moves `?token=` from the current URL into memory when on a token page.
 * Safe to call more than once. Returns the token in memory (or null).
 */
export function captureLinkTokenFromUrl(win = typeof window === 'undefined' ? undefined : window) {
  if (!win || !win.location || !matchesPage(win.location.pathname)) return tokenInMemory;
  let params;
  try {
    params = new URLSearchParams(win.location.search);
  } catch (_) {
    return tokenInMemory;
  }
  const token = params.get('token');
  if (!token) return tokenInMemory;
  tokenInMemory = token;
  params.delete('token');
  const query = params.toString();
  const clean = `${win.location.pathname}${query ? `?${query}` : ''}${win.location.hash || ''}`;
  try {
    win.history.replaceState(win.history.state, '', clean);
  } catch (_) {
    // History API unavailable; the token is still kept in memory.
  }
  return tokenInMemory;
}

export function linkTokenInMemory() {
  return tokenInMemory;
}

export function __resetLinkTokenForTests() {
  tokenInMemory = null;
}
